import {
    AppBar,
    Toolbar,
    Box,
    Button,
    Stack,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemText,
    Divider,
    useTheme,
    useMediaQuery,
    ListItemButton,
    Tooltip,
    Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import ChatIcon from '@mui/icons-material/Chat';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import AccountCircleIcon from '@mui/icons-material/AccountCircle'; // Icon for logged-in user
import LogoutIcon from '@mui/icons-material/Logout'; // Icon for logout
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { clearAuth } from '../utils/storage';
import logo from '../assets/Logo_Motto.png';
import Chatbot from './Chatbot';

interface HeaderProps {
    isLoggedIn: boolean;
    mode?: 'light' | 'dark';
    setMode?: (mode: 'light' | 'dark') => void;
}

const Header = ({ isLoggedIn, mode, setMode }: HeaderProps) => {
    const navigate = useNavigate();
    const theme = useTheme();
    // Use 'lg' for tablet/desktop cutoff for a cleaner tablet navigation experience
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [showChat, setShowChat] = useState(false);

    const handleDrawerToggle = () => setDrawerOpen(!drawerOpen);
    const handleLogout = () => {
        clearAuth();
        navigate('/login');
    };
    // Dark mode toggle function
    const handleModeToggle = () => {
        if (setMode) {
            setMode(mode === 'light' ? 'dark' : 'light');
        }
    };
    // Toggle Chatbot visibility
    const handleChatToggle = () => setShowChat(!showChat);


    const navItems = [
        { label: 'Buy Vehicle', path: '/vehicle-listing' },
        { label: 'Sell Vehicle', path: '/sell-vehicle' },
        { label: 'About', path: '/about' },
        { label: 'Article', path: '/#' },
        { label: 'Contact Us', path: '/contact-us' },
    ];

    // --- Helper for Navigation Buttons (Desktop) ---
    const renderNavButtons = () =>
        navItems.map((item) => (
            <Button
                key={item.path}
                onClick={() => navigate(item.path)}
                sx={{
                    fontWeight: 700, // Very bold font weight
                    fontSize: '1.1rem', // Increased font size for a bigger label
                    color: 'text.primary',
                    padding: '8px 16px', // Slightly increased padding to match the bigger text
                    borderRadius: 1,
                    // Enhanced hover effect
                    '&:hover': {
                        color: theme.palette.primary.main,
                        backgroundColor: theme.palette.action.hover,
                    },
                }}
            >
                {item.label}
            </Button>
        ));

    // --- Helper for Drawer Content (Mobile) ---
    const renderDrawerList = () => (
        <Box width={260} role="presentation" onClick={handleDrawerToggle}>
            <List>
                {/* Logo/Home link at the top of the drawer */}
                {/* <ListItem disablePadding sx={{ py: 1 }}>
                    <Box 
                        onClick={() => navigate('/')}
                        sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            px: 2, 
                            cursor: 'pointer' 
                        }}
                    >
                        <HomeIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="h6" fontWeight={700}>
                            WORK HORSE
                        </Typography>
                    </Box>
                </ListItem> */}
                <Divider />

                {/* Navigation Items */}
                {navItems.map((item) => (
                    <ListItem key={item.path} disablePadding>
                        <ListItemButton
                            onClick={() => navigate(item.path)}
                            sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
                        >
                            <ListItemText primary={item.label} />
                        </ListItemButton>
                    </ListItem>
                ))}
            </List>

            <Divider />

            {/* Action Buttons in Drawer */}
            <Box p={2}>
                <Stack spacing={1}>
                    {/* <Button 
                        fullWidth 
                        variant="text" 
                        onClick={handleModeToggle}
                        startIcon={mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
                        sx={{ justifyContent: 'flex-start' }}
                    >
                        {mode === 'dark' ? 'Light Mode' : 'Dark Mode'}
                    </Button> */}

                    {/* {isLoggedIn ? (
                        <>
                            <Button fullWidth variant="outlined" startIcon={<AccountCircleIcon />} onClick={() => navigate('/dashboard')}>
                                Dashboard
                            </Button>
                            <Button fullWidth variant="contained" color="error" startIcon={<LogoutIcon />} onClick={handleLogout}>
                                Logout
                            </Button>
                        </>
                    ) : (
                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            onClick={() => navigate('/login')}
                        >
                            Login / Sign Up
                        </Button>
                    )} */}
                </Stack>
            </Box>
        </Box>
    );

    return (
        <>
            <AppBar
                position="sticky" // Changed to sticky for better presence
                color="inherit"
                elevation={1} // Slight elevation for separation
                sx={{
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    backgroundColor: 'background.paper',
                }}
            >
                <Toolbar sx={{ justifyContent: 'space-between', py: 1 }}>
                    {/* --- Logo Area --- */}
                    <Box display="flex" alignItems="center" gap={1}>
                        <Box
                            component="img"
                            src={logo}
                            loading="lazy"
                            alt="WorkHorse Logo"
                            onClick={() => navigate('/')}
                            sx={{
                                width: { xs: 160, sm: 200, md: 260, lg: 300 },
                                cursor: 'pointer',
                                objectFit: 'contain',
                                transition: '0.2s',
                                '&:hover': { opacity: 0.85 },
                            }}

                        />
                    </Box>

                    {/* --- Desktop Navigation --- */}
                    {!isMobile && (
                        <Stack direction="row" spacing={3} alignItems="center">
                            {/* Primary Nav Buttons */}
                            <Stack direction="row" spacing={1} alignItems="center">
                                {renderNavButtons()}
                            </Stack>

                            {/* Divider to separate nav from actions */}
                            {/* <Divider orientation="vertical" flexItem sx={{ mx: 2 }} /> */}

                            {/* Action Buttons (Chat, Dark Mode, Auth) */}
                            <Stack direction="row" spacing={1} alignItems="center">
                                {/* Chatbot Toggle */}
                                {/* <Tooltip title={showChat ? "Hide Chat" : "Open Chatbot"}>
                                    <IconButton 
                                        onClick={handleChatToggle} 
                                        color={showChat ? "primary" : "inherit"}
                                        sx={{ 
                                            backgroundColor: showChat ? theme.palette.action.selected : 'transparent',
                                            '&:hover': { backgroundColor: theme.palette.action.hover }
                                        }}
                                    >
                                        <ChatIcon />
                                    </IconButton>
                                </Tooltip> */}

                                {/* Dark Mode Toggle */}
                                {/* <Tooltip title={mode === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}>
                                    <IconButton onClick={handleModeToggle} color="inherit">
                                        {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
                                    </IconButton>
                                </Tooltip> */}

                                {/* Auth Buttons */}
                                {/* {isLoggedIn ? (
                                    <>
                                        <Button 
                                            variant="text" 
                                            color="primary" 
                                            onClick={() => navigate('/dashboard')}
                                            startIcon={<AccountCircleIcon />}
                                            sx={{ fontWeight: 600 }}
                                        >
                                            Dashboard
                                        </Button>
                                        <Button 
                                            variant="outlined" 
                                            color="error" 
                                            size="small"
                                            onClick={handleLogout}
                                        >
                                            Logout
                                        </Button>
                                    </>
                                ) : (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() => navigate('/login')}
                                    >
                                        Are you a Member?
                                    </Button>
                                )} */}
                            </Stack>
                        </Stack>
                    )}

                    {/* --- Mobile Navigation --- */}
                    {isMobile && (
                        <Stack direction="row" spacing={1} alignItems="center">
                            {/* Dark Mode Toggle */}
                            {/* <Tooltip title={mode === 'dark' ? 'Light Mode' : 'Dark Mode'}>
                                <IconButton onClick={handleModeToggle} color="inherit">
                                    {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
                                </IconButton>
                            </Tooltip> */}
                            {/* Mobile Menu Icon */}
                            <IconButton
                                edge="end"
                                color="inherit"
                                onClick={handleDrawerToggle}
                                sx={{ ml: 1 }}
                            >
                                <MenuIcon />
                            </IconButton>
                            <Drawer
                                anchor="right"
                                open={drawerOpen}
                                onClose={handleDrawerToggle}
                                PaperProps={{
                                    sx: { boxShadow: theme.shadows[10] },
                                }}
                            >
                                {renderDrawerList()}
                            </Drawer>
                        </Stack>
                    )}
                </Toolbar>
            </AppBar>

            {/* --- Floating Chatbot --- */}
            {/* {showChat && (
                <Box
                    sx={{
                        position: 'fixed',
                        bottom: 24, // Moved up slightly from the bottom of the screen
                        right: 24,
                        zIndex: 1300, // Ensure it floats above most elements (Drawer is 1200)
                    }}
                >
                    <Chatbot />
                </Box>
            )} */}
        </>
    );
};

export default Header;